import { useState, useEffect, createContext, useContext } from 'react';
import { getProfileByEmail, upsertProfile } from '../services/api';

const AuthContext = createContext(null);

// Google OAuth configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    checkExistingSession();
    // Load Google OAuth script
    loadGoogleScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGoogleScript = () => {
    if (window.google) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  };

  const hydrateFromServer = async (baseUser) => {
    try {
      // Try to load server profile
      const serverProfile = await getProfileByEmail(baseUser.email);
      const merged = {
        ...baseUser,
        name: serverProfile.name || baseUser.name,
        picture: serverProfile.picture || baseUser.picture,
        role: serverProfile.role || baseUser.role || 'csm',
      };
      localStorage.setItem('user', JSON.stringify(merged));
      setUser(merged);
    } catch {
      // If not found, create it
      const created = await upsertProfile({
        email: baseUser.email,
        name: baseUser.name,
        picture: baseUser.picture || '',
        role: baseUser.role || 'csm',
      });
      const merged = { ...baseUser, ...created };
      localStorage.setItem('user', JSON.stringify(merged));
      setUser(merged);
    }
  };

  const checkExistingSession = async () => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('authToken');
    if (savedUser && token) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        // Non-blocking refresh from server to keep roles/picture in sync
        hydrateFromServer(parsed).finally(() => setLoading(false));
        return;
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      }
    }
    setLoading(false);
  };

  const login = async () => {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        reject(new Error('Google OAuth not loaded. Please refresh the page and try again.'));
        return;
      }
      if (!GOOGLE_CLIENT_ID) {
        reject(new Error('Google Client ID not configured'));
        return;
      }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            const userInfo = parseJwt(response.credential);
            const baseUser = {
              name: userInfo.name,
              email: userInfo.email,
              picture: userInfo.picture,
              initials: getInitials(userInfo.name),
              role: 'csm',
            };
            localStorage.setItem('user', JSON.stringify(baseUser));
            localStorage.setItem('authToken', response.credential);
            await hydrateFromServer(baseUser);
            resolve(baseUser);
          } catch (error) {
            console.error('OAuth callback error:', error);
            reject(error);
          }
        },
      });
      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'fixed';
      buttonContainer.style.top = '-9999px';
      document.body.appendChild(buttonContainer);
      window.google.accounts.id.renderButton(buttonContainer, { type: 'standard', size: 'large', theme: 'outline' });
      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('[role="button"]');
        if (googleButton) {
          googleButton.click();
          setTimeout(() => { document.body.removeChild(buttonContainer); }, 1000);
        } else {
          document.body.removeChild(buttonContainer);
          reject(new Error('Failed to initialize Google Sign-In button'));
        }
      }, 100);
    });
  };

  const saveProfile = async (partial) => {
    if (!user?.email) return;
    const toSave = { email: user.email, name: user.name, picture: user.picture, role: user.role, ...partial };
    const saved = await upsertProfile(toSave);
    const updated = { ...user, ...saved };
    localStorage.setItem('user', JSON.stringify(updated));
    setUser(updated);
    return updated;
  };

  const logout = async () => {
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    setUser(null);
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  // Helper function to parse JWT
  const parseJwt = (token) => {
    try { return JSON.parse(atob(token.split('.')[1])); } catch (e) { throw new Error('Invalid token'); }
  };
  const getInitials = (name) => name.split(' ').map(part => part.charAt(0).toUpperCase()).join('').substring(0, 2);

  const value = { user, login, logout, loading, isAuthenticated: !!user, saveProfile };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) { throw new Error('useAuth must be used within an AuthProvider'); }
  return context;
};